import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

import {
  AdminAssessmentFilterStatus,
  AdminAssessmentFlaggedFilter,
  AdminAssessmentScoreBand,
  AdminHtsSubmissionFilterStatus,
  HtsReadinessBand,
  type AdminAssessmentResultsQuery,
  type AdminHtsSubmissionResultsQuery,
} from '@evaltick/shared';

const DATE_PARAM_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** A picked day as it's kept in the URL — `YYYY-MM-DD`, in the admin's own timezone. */
export const DATE_PARAM_FORMAT = 'YYYY-MM-DD';

const parseDateParam = (value: string | null): string | undefined =>
  value && DATE_PARAM_PATTERN.test(value) && dayjs(value).isValid() ? value : undefined;

const capitalize = (field: string) => field.charAt(0).toUpperCase() + field.slice(1);

/**
 * URL-param mechanics shared by both Results lists' filter hooks: reading a
 * namespaced `search`/date-range/page plus a set of single-value "extra"
 * fields (each optionally restricted to a known set of values, so a
 * hand-edited or stale URL can't smuggle in garbage), and writing changes
 * back by replacing the current history entry — never pushing one per
 * keystroke — so a filtered view survives "View results" → back and can be
 * bookmarked or shared.
 */
function useNamespacedResultsParams<TExtraField extends string>(
  prefix: string,
  extraFields: readonly TExtraField[],
  validValues: Partial<Record<TExtraField, readonly string[]>> = {},
) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const keyFor = useCallback((field: string) => `${prefix}${capitalize(field)}`, [prefix]);

  const search = searchParams.get(keyFor('search')) ?? '';
  const extra = useMemo(() => {
    const result = {} as Record<TExtraField, string | undefined>;
    for (const field of extraFields) {
      const raw = searchParams.get(keyFor(field)) ?? undefined;
      const allowed = validValues[field];
      result[field] = !raw || (allowed && !allowed.includes(raw)) ? undefined : raw;
    }
    return result;
  }, [searchParams, keyFor, extraFields, validValues]);

  let from = parseDateParam(searchParams.get(keyFor('from')));
  let to = parseDateParam(searchParams.get(keyFor('to')));
  if (from && to && from > to) {
    from = undefined;
    to = undefined;
  }
  const pageParam = Number(searchParams.get(keyFor('page')));
  const page = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

  // Builds on the live URL, not this render's searchParams: a debounced search
  // or an in-flight response can write well after the render that scheduled
  // it, and a stale snapshot would silently undo whatever changed in between
  // (the other tab's filters, the active tab). `navigate` also keeps a stable
  // identity across query-string changes — unlike setSearchParams — so writers
  // built on it don't retrigger effects in every mounted list on each change.
  // Keyed by plain `string` rather than `'search' | 'from' | 'to' | 'page' |
  // TExtraField`: TS can't verify a partial object literal against a mapped
  // type whose key union includes a bare, unresolved generic parameter. The
  // public per-tab hooks below re-expose these with their own precise types.
  const writeParams = useCallback(
    (entries: Partial<Record<string, string | undefined>>) => {
      const next = new URLSearchParams(window.location.search);
      for (const [field, value] of Object.entries(entries)) {
        const key = keyFor(field);
        if (value) next.set(key, value);
        else next.delete(key);
      }
      const query = next.toString();
      void navigate({ search: query ? `?${query}` : '' }, { replace: true });
    },
    [keyFor, navigate],
  );

  const updateFilters = useCallback(
    (patch: Partial<Record<string, string | undefined>>) => writeParams({ ...patch, page: undefined }),
    [writeParams],
  );

  const setPage = useCallback(
    (nextPage: number) => writeParams({ page: nextPage > 1 ? String(nextPage) : undefined }),
    [writeParams],
  );

  const clearFilters = useCallback(() => {
    const cleared: Partial<Record<string, string | undefined>> = {
      search: undefined,
      from: undefined,
      to: undefined,
      page: undefined,
    };
    for (const field of extraFields) cleared[field] = undefined;
    writeParams(cleared);
  }, [writeParams, extraFields]);

  return { search, extra, from, to, page, updateFilters, setPage, clearFilters };
}

const ASSESSMENTS_EXTRA_FIELDS = ['status', 'flagged', 'scoreBand'] as const;
const ASSESSMENTS_VALID_VALUES = {
  status: Object.values(AdminAssessmentFilterStatus),
  flagged: Object.values(AdminAssessmentFlaggedFilter),
  scoreBand: Object.values(AdminAssessmentScoreBand),
};

export interface AssessmentsResultsFilterValues {
  search: string;
  status?: AdminAssessmentFilterStatus;
  flagged?: AdminAssessmentFlaggedFilter;
  scoreBand?: AdminAssessmentScoreBand;
  /** `YYYY-MM-DD` — see DATE_PARAM_FORMAT. */
  from?: string;
  /** `YYYY-MM-DD` — see DATE_PARAM_FORMAT. */
  to?: string;
}

export interface UseAssessmentsResultsFiltersReturn {
  filters: AssessmentsResultsFilterValues;
  page: number;
  /** What the assessments list endpoint takes — the day range widened to full local days as ISO timestamps. */
  apiFilters: AdminAssessmentResultsQuery;
  hasActiveFilters: boolean;
  /** Applies a filter change and goes back to page 1, since the old page may now be out of range. */
  updateFilters: (patch: Partial<AssessmentsResultsFilterValues>) => void;
  setPage: (page: number) => void;
  clearFilters: () => void;
}

/** The Assessments tab's filters and page, kept in the URL under `prefix`-namespaced keys. */
export function useAssessmentsResultsFilters(prefix: string): UseAssessmentsResultsFiltersReturn {
  const { search, extra, from, to, page, updateFilters, setPage, clearFilters } = useNamespacedResultsParams(
    prefix,
    ASSESSMENTS_EXTRA_FIELDS,
    ASSESSMENTS_VALID_VALUES,
  );

  const status = extra.status as AdminAssessmentFilterStatus | undefined;
  const flagged = extra.flagged as AdminAssessmentFlaggedFilter | undefined;
  const scoreBand = extra.scoreBand as AdminAssessmentScoreBand | undefined;

  const filters = useMemo<AssessmentsResultsFilterValues>(
    () => ({ search, status, flagged, scoreBand, from, to }),
    [search, status, flagged, scoreBand, from, to],
  );

  const apiFilters = useMemo<AdminAssessmentResultsQuery>(
    () => ({
      search: search.trim() || undefined,
      status,
      flagged,
      scoreBand,
      from: from ? dayjs(from).startOf('day').toISOString() : undefined,
      to: to ? dayjs(to).endOf('day').toISOString() : undefined,
    }),
    [search, status, flagged, scoreBand, from, to],
  );

  const hasActiveFilters = Boolean(search.trim() || status || flagged || scoreBand || from || to);

  return { filters, page, apiFilters, hasActiveFilters, updateFilters, setPage, clearFilters };
}

const HTS_SUBMISSIONS_EXTRA_FIELDS = ['status', 'band'] as const;
const HTS_SUBMISSIONS_VALID_VALUES = {
  status: Object.values(AdminHtsSubmissionFilterStatus),
  band: Object.values(HtsReadinessBand),
};

export interface HtsSubmissionsResultsFilterValues {
  search: string;
  status?: AdminHtsSubmissionFilterStatus;
  /** Same value the Score and Readiness filters both drive — see HTS_READINESS_BAND_OPTIONS. */
  band?: HtsReadinessBand;
  /** `YYYY-MM-DD` — see DATE_PARAM_FORMAT. */
  from?: string;
  /** `YYYY-MM-DD` — see DATE_PARAM_FORMAT. */
  to?: string;
}

export interface UseHtsSubmissionsResultsFiltersReturn {
  filters: HtsSubmissionsResultsFilterValues;
  page: number;
  /** What the HTS submissions list endpoint takes — the day range widened to full local days as ISO timestamps. */
  apiFilters: AdminHtsSubmissionResultsQuery;
  hasActiveFilters: boolean;
  /** Applies a filter change and goes back to page 1, since the old page may now be out of range. */
  updateFilters: (patch: Partial<HtsSubmissionsResultsFilterValues>) => void;
  setPage: (page: number) => void;
  clearFilters: () => void;
}

/** The HTS Submissions tab's filters and page, kept in the URL under `prefix`-namespaced keys. */
export function useHtsSubmissionsResultsFilters(prefix: string): UseHtsSubmissionsResultsFiltersReturn {
  const { search, extra, from, to, page, updateFilters, setPage, clearFilters } = useNamespacedResultsParams(
    prefix,
    HTS_SUBMISSIONS_EXTRA_FIELDS,
    HTS_SUBMISSIONS_VALID_VALUES,
  );

  const status = extra.status as AdminHtsSubmissionFilterStatus | undefined;
  const band = extra.band as HtsReadinessBand | undefined;

  const filters = useMemo<HtsSubmissionsResultsFilterValues>(
    () => ({ search, status, band, from, to }),
    [search, status, band, from, to],
  );

  const apiFilters = useMemo<AdminHtsSubmissionResultsQuery>(
    () => ({
      search: search.trim() || undefined,
      status,
      band,
      from: from ? dayjs(from).startOf('day').toISOString() : undefined,
      to: to ? dayjs(to).endOf('day').toISOString() : undefined,
    }),
    [search, status, band, from, to],
  );

  const hasActiveFilters = Boolean(search.trim() || status || band || from || to);

  return { filters, page, apiFilters, hasActiveFilters, updateFilters, setPage, clearFilters };
}
